import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { body2, colors, fontSizes, spacing } from 'ui/theme';
import { PrimaryButton } from 'ui/button';
import { StyledWrapper } from 'ui/inputs/InputStyles';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { useModalContext } from 'utils/contexts/modal';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { checkForSpecialCharacters } from '../utils';

import { AddAccountModalState } from '../types';
import { useIntegrationContext } from '../contexts/IntegrationContext';
import { useTranslations } from '../hooks/useTranslations';

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};

  ${StyledWrapper} {
    :focus-within, &[data-show-focused='true'] {
      &[data-error='true'] {
        box-shadow: 0px 0px 0px 1px ${colors.red100};
        border-color: ${colors.red100};
        & .input-prefix svg {
          color: ${colors.red100};
        }
      }
    }
  }
`;

const ErrorMessage = styled.div`
  ${body2}
  font-size: ${fontSizes.s12};
  color: ${colors.textDestructive};
`;

export const testIds = {
  modal: 'mi-add-modal',
  modalInput: 'mi-add-modal-input',
  miAccountNameInput: 'mi-add-modal-account-name-input',
  modalConfirmBtn: 'mi-add-modal-confirm-btn',
  modalError: 'mi-add-modal-error',
  emailError: 'mi-add-modal-email-error',
};

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

export const AddAccountModal = () => {
  const { t } = useTranslations();
  const { hideModal, modalState } = useModalContext<AddAccountModalState>();
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const { addIntegration, integrations } = useIntegrationContext();

  const isEmailInUse = useCallback(
    (email: string) => {
      return integrations.some((integration) => integration?.accountId === email);
    },
    [integrations],
  );

  const hasError = useMemo(() => checkForSpecialCharacters(name), [name]);
  const hasEmailError = useMemo(() => {
    const trimmedEmail = email.trim();
    return isEmailInUse(trimmedEmail);
  }, [email, isEmailInUse]);

  const onAdd = () => {
    if (hasError && hasEmailError) return;
    if (modalState?.code) {
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      addIntegration(modalState.code, trimmedName, trimmedEmail);
    }
    hideModal();
  };

  const isAddButtonDisabled = useMemo(() => {
    return !name || !email || hasError || hasEmailError;
  }, [name, email, hasError, hasEmailError]);

  return (
    <Modal
      id="MovableInkIntegration-AddModal"
      data-test={testIds.modal}
      isModalOpen={true}
      onClose={hideModal}
    >
      <ModalHeader>{t('modal.add.header')}</ModalHeader>
      <StyledModalBody>
        <p>{t('modal.add.content')}</p>
        <FormField
          labelText={t('input.addAccountName.label')}
          hasError={hasError}
          restrictionText={`${name.length}/255 characters`}
          isRequired
        >
          <InputWrapper data-input-error={hasError}>
            <InputText
              data-test={testIds.modalInput}
              hasIcon
              hasError={hasError}
              maxLength={255}
              onChange={setName}
              value={name}
            />
            {hasError && (
              <ErrorMessage data-test={testIds.modalError}>{t('modal.add.error')}</ErrorMessage>
            )}
          </InputWrapper>
        </FormField>
        <FormField labelText={t('input.addAccountName.email')} isRequired hasError={hasEmailError}>
          <InputWrapper data-input-error={hasEmailError}>
            <InputText
              data-test={testIds.miAccountNameInput}
              hasIcon
              maxLength={255}
              onChange={setEmail}
              value={email}
              hasError={hasEmailError}
            />
            {hasEmailError && (
              <ErrorMessage data-test={testIds.emailError}>
                {t('modal.add.emailError')}
              </ErrorMessage>
            )}
          </InputWrapper>
        </FormField>
      </StyledModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton
          isDisabled={isAddButtonDisabled}
          data-test={testIds.modalConfirmBtn}
          onClick={onAdd}
        >
          {t('modal.add.confirm')}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
